<template>
  <div class="col-sm-12">
    <div class="card">
      <div class="dropdown-item page-item">
        <div class="page-grid">
          <div class="page-search-container dropdown-item">
            <input
              type="text"
              placeholder="Search by Item Name or SAP Code..."
              class="search-input"
              v-model="searchFilter"
            />
          </div>

          <button type="button" class="btn button-default new-item-btn" @click="newItem">
            Create Item
          </button>
        </div>
      </div>

      <div v-if="loading" class="loading-spinner">
        <i class="fa fa-spinner fa-spin"></i> Loading...
      </div>

      <div v-if="!loading" class="table-responsive">
        <table class="table">
          <thead>
            <tr class="border-bottom-primary">
              <th scope="col">Sap Code</th>
              <th scope="col">Item Name</th>
              <th scope="col">Item Category</th>
              <th scope="col">No of Variants</th>
              <th scope="col">Status</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in filteredItems" :key="item.id">
              <td>{{ item.sap_code}}</td>
              <td>{{ item.item_name}}</td>
              <td>{{ item.item_category }}</td>
              <td>{{ item.no_of_variants }}</td>
              <td>
                <span :class="statusClass(item.status)">
                  {{ item.status === "1" ? 'ACTIVE' : 'INACTIVE' }}
                </span>
              </td>
              <td>
                <i class="fa fa-edit action-icon" @click="editDetails(item.item_id)"></i>
                <i class="fa fa-eye action-icon" aria-hidden="true" @click="viewDetails(item.item_id)"></i>
              </td>
            </tr>
            <tr v-if="filteredItems.length === 0">
              <td colspan="6" class="text-center">No data available</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="loadingPopup" class="fullscreen-loader">
      <div class="spinner"></div>
    </div>

    <ItemDetailsModal 
      v-if="showPopup" 
      :itemDetails="itemDetails" 
      :itemVariants="itemVariants" 
      @close-modal="closePopup" 
    />
    
  </div>
</template>

<script>
import { useRouter } from "vue-router";
import { fetchAllItemsList, ViewItemsDetailsById } from "@/services/dataService";

// Import the newly created Modal Component
import ItemDetailsModal from "@/pages/Modal/itemDetailsModal.vue";

export default {
  name: "ItemListScreen",
  components: {
    ItemDetailsModal
  },
  data() {
    return {
      router: useRouter(),
      loading: false,
      loadingPopup: false,
      showPopup: false,
      searchFilter: "",
      token: localStorage.getItem("token_set") || "",
      profileId: localStorage.getItem("profile_id") || "",
      itemList: [],
      itemDetails: {},
      itemVariants: [],
    };
  },

  computed: {
    // Search Filter Logic
    filteredItems() {
      const filter = this.searchFilter.toLowerCase();
      return [...this.itemList]
        .filter(item =>
          (item.item_name && item.item_name.toLowerCase().includes(filter)) ||
          (item.sap_code && item.sap_code.toLowerCase().includes(filter))
        )
        .sort((a, b) => a.item_id - b.item_id);
    }
  },

  watch: {
    // Lock background scroll when fetching details API
    loadingPopup() {
      this.toggleBodyScroll();
    }
  },

  created() {
    this.fetchItems();
    this.toggleBodyScroll();
  },

  beforeUnmount() {
    // Ensure scroll is restored when leaving page
    document.body.style.overflow = ''; 
  },

  methods: {
    toggleBodyScroll() {
      if (this.loadingPopup) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },

    // Fetch the main list
    async fetchItems() {
      this.loading = true;
      try {
        const response = await fetchAllItemsList(1, this.token);
        this.itemList = response.data.data || [];
      } catch (error) {
        console.error("Error fetching item list:", error);
        this.itemList = [];
      } finally {
        this.loading = false;
      }
    },

    newItem() {
      this.router.push({ path: "/create-item" });
    },

    editDetails(id) {
      this.router.push({ path: `/edit-item/${id}` });
    },

    // Fetch single item details and pass to Modal
    async viewDetails(id) {
      try {
        this.loadingPopup = true;
        this.itemDetails = {};
        this.itemVariants = [];

        const response = await ViewItemsDetailsById(id, this.token);
        const item = response.data.data;

        this.itemDetails = item.item_details;
        this.itemVariants = item.item_variants;

        this.showPopup = true;
      } catch (error) {
        console.error("Error fetching item details:", error);
      } finally {
        this.loadingPopup = false;
      }
    },

    closePopup() {
      this.showPopup = false;
    },

    statusClass(status) {
      if (status === "0") return 'badge badge-cancel';
      if (status === "1") return 'badge badge-active';
      return 'badge badge-secondary';
    }
  }
};
</script>

<style scoped>
.card {
  padding: 10px;
}

.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

.search-input {
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
  width: 100%;
}

.search-input:hover, .search-input:focus {
  border-color: #0056b3;
  outline: none;
}

.page-grid {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.page-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
}

.page-search-container {
  flex-grow: 1;
  margin-right: 15px;
}

.new-item-btn {
  margin-left: auto;
  padding: 10px 20px;
}

/* Action Icons */
.action-icon {
  cursor: pointer;
  transition: color 0.3s, transform 0.2s;
  margin-right: 12px;
  font-size: 16px;
  color: #555;
}

.action-icon:hover {
  color: #0056b3;
  transform: scale(1.2);
}

.action-icon:active {
  transform: scale(1.1);
}

/* Badges */
.badge-cancel {
  background-color: #dc3545;
  color: white;
  padding: 5px 10px;
  border-radius: 5px;
  font-size: 12px;
}

.badge-active {
  background-color: #28a745;
  color: white;
  padding: 5px 10px;
  border-radius: 5px;
  font-size: 12px;
}

.badge-secondary {
  background-color: #6c757d;
  color: white;
  padding: 5px 10px;
  border-radius: 5px;
  font-size: 12px;
}

/* Fullscreen Loader for API calls */
.fullscreen-loader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.spinner {
  width: 60px;
  height: 60px;
  border: 5px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 1s ease infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>